import React, { useState } from "react";
import Button from "src/components/common/Button";
import CheckBox from "src/components/common/CheckBox";
import styles from "./styles.module.scss";

// Same status colouring as the market-data schedule: last-run outcome, bold
// against the dark card; "Pending (first run)" / unexpected stay neutral.
const STATUS_COLORS = {
    Healthy: "#3bd4ae", // textHighlight
    Failed: "#ff2e5b", // textError
};
const statusColor = (status) => STATUS_COLORS[status] || "#cae1da"; // textLight

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

// ISO instant -> "YYYY-MM-DD hh:mm PM KST" in Korea time (see MarketDataSchedule).
const fmtTime = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const parts = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Asia/Seoul",
        year: "numeric", month: "2-digit", day: "2-digit",
        hour: "2-digit", minute: "2-digit", hour12: true,
    }).formatToParts(d);
    const get = (t) => parts.find((p) => p.type === t)?.value || "";
    const period = get("dayPeriod").replace(/\./g, "").toUpperCase();
    return `${get("year")}-${get("month")}-${get("day")} ${get("hour")}:${get("minute")} ${period} KST`;
};

// Friendly labels + a one-line description of what each command does when it
// fires. The command names are the CommandType enum values command-gateway
// dispatches on; scheduler-service publishes them on the shared commands topic.
export const COMMAND_LABELS = {
    AUTOROLL_INVENTORY: "Auto-roll inventory",
    CLEANUP_CONFIRMS_BY_SECURITY: "Clean up confirms-by-security",
    EMIT_CONFIRMS_VALUE_DATE_REACHED: "Emit value-date-reached confirms",
};
export const COMMAND_HINTS = {
    AUTOROLL_INVENTORY: "Rolls (or expires) every cpty's inventory to the next value date.",
    CLEANUP_CONFIRMS_BY_SECURITY: "Deletes confirms-by-security entries older than a month.",
    EMIT_CONFIRMS_VALUE_DATE_REACHED: "Emits confirmations for trades whose value date has been reached.",
};
export const commandLabel = (cmd) => COMMAND_LABELS[cmd] || cmd;

export const TIMEZONES = ["Asia/Seoul", "Asia/Tokyo", "UTC", "Europe/London", "America/New_York"];
export const CADENCES = [
    { id: "daily", label: "Every day" },
    { id: "weekdays", label: "Weekdays (Mon–Fri)" },
    { id: "weekly", label: "Weekly (pick days)" },
    { id: "monthly", label: "Monthly (nth weekday)" },
];
export const DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];
export const NTH = [
    { id: 1, label: "1st" },
    { id: 2, label: "2nd" },
    { id: 3, label: "3rd" },
    { id: 4, label: "4th" },
    { id: 5, label: "5th" },
];

// The schedule a command falls back to before the operator ever configures it:
// disabled, so nothing fires until they opt in and confirm.
export const defaultSchedule = () => ({
    enabled: false,
    time: "18:00",
    timezone: "Asia/Seoul",
    cadence: "daily",
    daysOfWeek: [],
    nthWeekday: 1,
});

/**
 * Operator-managed recurring platform-command schedules (issue #31). Unlike the
 * market-data schedule these are a FIXED set of commands (no add/delete) - one
 * row each, with an enable toggle, a run time, a timezone, and a cadence
 * (daily / weekdays / weekly / monthly). Presentational only: pending edits live
 * in the parent page and apply through its single confirm/discard bar.
 */
const CommandSchedules = ({ commands = [], schedules = {}, statuses = {}, onEdit, onViewHistory, onRunNow }) => {
    // Which command's history panel is open, plus its loaded runs (one at a time).
    const [openCmd, setOpenCmd] = useState(null);
    const [runs, setRuns] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState(null);

    const toggleHistory = async (cmd) => {
        if (openCmd === cmd) {
            setOpenCmd(null);
            return;
        }
        setOpenCmd(cmd);
        setRuns([]);
        setHistoryError(null);
        setHistoryLoading(true);
        try {
            const res = await onViewHistory(cmd);
            setRuns(res?.runs || []);
        } catch (e) {
            setHistoryError(e.message || "Failed to load history");
        } finally {
            setHistoryLoading(false);
        }
    };

    // Toggle one weekday in a command's daysOfWeek (weekly cadence).
    const toggleDay = (cmd, day, current) => {
        const set = new Set(current || []);
        if (set.has(day)) set.delete(day);
        else set.add(day);
        // Keep MON..SUN order so the saved config is stable/diff-friendly.
        onEdit(cmd, { daysOfWeek: DAYS.filter((d) => set.has(d)) });
    };

    return (
        <div className={styles.section}>
            <div className={styles.sectionTitle}>Scheduled commands</div>
            <div className={styles.configForm}>
                {commands.length === 0 && <div className={styles.configHint}>No schedulable commands.</div>}
                {commands.length > 0 && (
                    <div className={styles.scheduleGrid}>
                        {commands.map((cmd) => {
                            const s = schedules[cmd] || defaultSchedule();
                            const status = statuses[cmd];
                            const open = openCmd === cmd;
                            return (
                                <React.Fragment key={cmd}>
                                    <div className={styles.scheduleRow}>
                                        <span className={styles.scheduleId} title={COMMAND_HINTS[cmd] || ""}>
                                            {commandLabel(cmd)}
                                        </span>
                                        <CheckBox
                                            name={"cmd-en-" + cmd}
                                            selected={!!s.enabled}
                                            onChange={() => onEdit(cmd, { enabled: !s.enabled })}
                                            small
                                            styleName="black"
                                            dataTestId={"cmdsched-enabled-" + cmd}
                                        >
                                            <span className={styles.configHint}>Enabled</span>
                                        </CheckBox>
                                        <input
                                            type="time"
                                            className={styles.timeInput}
                                            value={s.time || "18:00"}
                                            onChange={(e) => onEdit(cmd, { time: e.target.value })}
                                            data-testid={"cmdsched-time-" + cmd}
                                        />
                                        <select
                                            className={styles.configSelect}
                                            value={s.cadence || "daily"}
                                            onChange={(e) => onEdit(cmd, { cadence: e.target.value })}
                                            data-testid={"cmdsched-cadence-" + cmd}
                                        >
                                            {CADENCES.map((c) => (
                                                <option key={c.id} value={c.id}>{c.label}</option>
                                            ))}
                                        </select>
                                        <select
                                            className={styles.configSelect}
                                            value={s.timezone || "Asia/Seoul"}
                                            onChange={(e) => onEdit(cmd, { timezone: e.target.value })}
                                            data-testid={"cmdsched-tz-" + cmd}
                                        >
                                            {TIMEZONES.map((tz) => (
                                                <option key={tz} value={tz}>{tz}</option>
                                            ))}
                                        </select>
                                        <span className={styles.configHint}
                                              style={status ? { color: statusColor(status), fontWeight: 600 } : undefined}>
                                            {status || ""}
                                        </span>
                                        <Button
                                            value={open ? "Hide History" : "View History"}
                                            onClick={() => toggleHistory(cmd)}
                                            dataTestId={"cmdsched-history-" + cmd}
                                        />
                                        <Button
                                            value="Run Now"
                                            onClick={() => onRunNow(cmd)}
                                            dataTestId={"cmdsched-runnow-" + cmd}
                                        />
                                    </div>

                                    {/* Weekly: pick the weekdays it fires on. */}
                                    {s.cadence === "weekly" && (
                                        <div className={styles.cadenceDetail}>
                                            <span className={styles.configHint}>Fires on:</span>
                                            {DAYS.map((day) => (
                                                <CheckBox
                                                    key={day}
                                                    name={"cmd-day-" + cmd + "-" + day}
                                                    selected={(s.daysOfWeek || []).includes(day)}
                                                    onChange={() => toggleDay(cmd, day, s.daysOfWeek)}
                                                    small
                                                    styleName="black"
                                                    dataTestId={"cmdsched-day-" + cmd + "-" + day}
                                                >
                                                    <span className={styles.configHint}>{day}</span>
                                                </CheckBox>
                                            ))}
                                        </div>
                                    )}

                                    {/* Monthly: the nth occurrence of one weekday each month. */}
                                    {s.cadence === "monthly" && (
                                        <div className={styles.cadenceDetail}>
                                            <span className={styles.configHint}>On the</span>
                                            <select
                                                className={styles.configSelect}
                                                value={s.nthWeekday || 1}
                                                onChange={(e) => onEdit(cmd, { nthWeekday: Number(e.target.value) })}
                                                data-testid={"cmdsched-nth-" + cmd}
                                            >
                                                {NTH.map((n) => (
                                                    <option key={n.id} value={n.id}>{n.label}</option>
                                                ))}
                                            </select>
                                            <select
                                                className={styles.configSelect}
                                                value={(s.daysOfWeek && s.daysOfWeek[0]) || "MON"}
                                                onChange={(e) => onEdit(cmd, { daysOfWeek: [e.target.value] })}
                                                data-testid={"cmdsched-monthly-day-" + cmd}
                                            >
                                                {DAYS.map((day) => (
                                                    <option key={day} value={day}>{day}</option>
                                                ))}
                                            </select>
                                            <span className={styles.configHint}>of the month</span>
                                        </div>
                                    )}

                                    {open && (
                                        <div className={styles.historyPanel}>
                                            {historyLoading && <div className={styles.configHint}>Loading...</div>}
                                            {historyError && (
                                                <div className={styles.configHint} style={{ color: statusColor("Failed") }}>
                                                    {historyError}
                                                </div>
                                            )}
                                            {!historyLoading && !historyError && runs.length === 0 && (
                                                <div className={styles.configHint}>No runs recorded yet.</div>
                                            )}
                                            {!historyLoading && runs.length > 0 && (
                                                <table className={styles.historyTable}>
                                                    <thead>
                                                        <tr>
                                                            <th>Scheduled for</th>
                                                            <th>Ran at</th>
                                                            <th>Status</th>
                                                            <th>Detail</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {runs.map((run, i) => (
                                                            <tr key={i}>
                                                                <td>{fmtTime(run.scheduledFor)}</td>
                                                                <td>{fmtTime(run.runAt)}</td>
                                                                <td style={{ color: statusColor(cap(run.status)), fontWeight: 600 }}>
                                                                    {cap(run.status)}
                                                                </td>
                                                                <td className={styles.historyDetail}>{run.detail || "-"}</td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            )}
                                        </div>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommandSchedules;
